import { FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getSetupPosDisplayName } from '../../../types/RevenueSource';
import { PosInput, savePos, SetupStep, SETUP_POS_PROVIDERS } from '../../../services/accountSetup/accountSetupService';
import { ShieldCheck } from 'lucide-react';
import { lightGreenBG, textDarkGreen, secondaryText } from '../../../styling/stylingHelper';
import { setupButton, setupErrorKey, SetupError, setupInput, SetupModal } from './SetupPrimitives';
export default function SetupPosForm({
  step,
  onChange,
  onClose,
  alias
}: {
  step: SetupStep;
  alias?: string;
  onChange: () => Promise<void>;
  onClose: () => void;
}) {
  const {
    t
  } = useTranslation();
  const [form, setForm] = useState<PosInput>({
    provider: step.pos?.provider || '',
    otherProvider: step.pos?.otherProvider || '',
    username: step.pos?.username || '',
    password: '',
    portalUrl: step.pos?.portalUrl || '',
    venueName: step.pos?.venueName || ''
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const change = (key: keyof PosInput, value: string) => {
    setForm(current => ({
      ...current,
      [key]: value
    }));
  };
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      await savePos(step.restaurantId, form);
      setForm(current => ({
        ...current,
        password: ''
      }));
      await onChange();
      onClose();
    } catch (failure) {
      setError(setupErrorKey(failure));
    } finally {
      setBusy(false);
    }
  };
  return <SetupModal title={t('accountSetup.steps.pos.title')} description={t('accountSetup.steps.pos.description')} onClose={onClose} busy={busy}><form onSubmit={event => void submit(event)} className="space-y-4">
    <SetupPosAccess alias={alias} />
    <label className="block text-sm font-medium">{t('accountSetup.pos.provider')}<select required value={form.provider} disabled={busy} onChange={event => change('provider', event.target.value)} className={`${setupInput} mt-1`}><option value="">{t('accountSetup.pos.choose')}</option>{(form.provider === 'arcdrinks' ? [...SETUP_POS_PROVIDERS, 'arcdrinks'] : SETUP_POS_PROVIDERS).map(source => <option key={source} value={source}>{getSetupPosDisplayName(source, t('accountSetup.pos.dishName'))}</option>)}<option value="other">{t('accountSetup.pos.other')}</option></select></label>
    {form.provider === 'other' && <label className="block text-sm font-medium">{t('accountSetup.pos.otherName')}<input required maxLength={160} value={form.otherProvider} disabled={busy} onChange={event => change('otherProvider', event.target.value)} className={`${setupInput} mt-1`} /></label>}
    <div className="grid gap-4 sm:grid-cols-2">{(['username', 'password', 'portalUrl', 'venueName'] as const).map(field => <label key={field} className="block text-sm font-medium">{t(`accountSetup.pos.${field}`)}<input type={field === 'password' ? 'password' : field === 'portalUrl' ? 'url' : 'text'} autoComplete={field === 'password' ? 'new-password' : 'off'} maxLength={{ password: 4096, username: 320, portalUrl: 2048, venueName: 200 }[field]} required={field === 'username' || (field === 'password' && !step.pos?.hasPassword)} value={form[field] || ''} disabled={busy} onChange={event => change(field, event.target.value)} className={`${setupInput} mt-1`} /></label>)}</div>
    {step.pos?.hasPassword && <p className={`text-sm ${secondaryText}`}>{t('accountSetup.pos.passwordSaved')}</p>}
    <div className={`flex gap-3 rounded-xl p-4 ${lightGreenBG}`}><ShieldCheck className={`mt-0.5 h-5 w-5 shrink-0 ${textDarkGreen}`} /><div><h3 className="text-sm font-medium">{t('accountSetup.pos.protectedTitle')}</h3><p className={`mt-1 text-xs leading-5 ${secondaryText}`}>{t('accountSetup.pos.protectedDescription')}</p></div></div>
    <SetupError message={error} />
    <button disabled={busy} className={`${setupButton} w-full`} type="submit">{t(busy ? 'accountSetup.saving' : 'accountSetup.pos.save')}</button>
  </form></SetupModal>;
}

export function SetupPosAccess({ alias }: { alias?: string }) {
  const { t } = useTranslation();
  return <div className={`rounded-xl p-4 ${lightGreenBG}`}><h3 className="text-sm font-medium">{t('accountSetup.pos.accessTitle')}</h3><p className={`mt-2 text-sm ${secondaryText}`}>{t('accountSetup.pos.accessHelp')}</p><p className="mt-2 break-all text-sm font-medium">{alias || t('accountSetup.aliasFallback')}</p></div>;
}
